// External
import { Injectable } from '@angular/core';

// Internal
import { DevicesService } from '../../process/devices/devices.service';
import { UtilsCommonService } from '../../../../common/utils/utils-common.service';
import { ValuesService } from '../../../../common/values/values.service';
import { SubscriptionsService } from '../../process/subscriptions/subscriptions.service';
import { DevicesValuesService } from '../../../../common/values/devices.values';
import { ProfilesService } from '../../process/profiles/profiles.service';
import { UsefulService } from '../../global/useful/useful.service';
import { SubscriptionsHelperService } from '../../../../pages/subscriptions/subscriptions.helper.service';
import { ParentalValuesService } from '../../../../common/values/parental.values.service';
import { MessageService } from '../../core/message.service';
import { SettingsService } from '../../../services/process/settings/settings.service';
import { LinksService } from '../../../../common/links/links.service';
import { IParentalService } from '../iparental/iparental.service';
import { tv4 } from 'tv4';
import { schemas } from 'src/app/common/models/schemas';


@Injectable({
    providedIn: 'root'
})

export class IDevicesService {

    status = {
        ready: false,
        risks: {
            atRisk: false,
            hasVulnerability: false,
            hasIssues: false
        },
        details: {
            hasDetails: false,
            hasActions: false,
            bdProtected: false
        },
        onBox: {
            disabled: true,
            isConnected: false,
            isProtected: false,
            showLastSeenTime: false
        },
        outsideBox: {
            isProtected: false
        },
    };

    constructor(
        readonly subscriptionsHelperService: SubscriptionsHelperService,
        readonly parentalValuesService: ParentalValuesService,
        readonly devicesService: DevicesService,
        readonly devicesValuesService: DevicesValuesService,
        readonly profilesService: ProfilesService,
        readonly subscriptionsService: SubscriptionsService,
        readonly usefulService: UsefulService,
        readonly utilsService: UtilsCommonService,
        readonly valuesService: ValuesService,
        readonly messageService: MessageService,
        readonly settingsService: SettingsService,
        readonly linksService: LinksService,
        readonly iParentalService: IParentalService
    ) { }

    checkAppStatus(device) {
        // unsupported os
        if (!this.parentalValuesService.NON_IOT_PLATFORMS.has(device.device_os)) {
            return this.parentalValuesService.appStatus.unsupported;
        // ios & android
        } else if (this.parentalValuesService.NON_AGENT_PLATFORMS.has(device.device_os)) {
            // no agent - has parental installed
            if (this.iParentalService.hasParentalNoAgent(device)) {
                return this.parentalValuesService.appStatus.installed;
            // no agent - does not have parental installed, open install modal, send link step
            } else {
                return this.parentalValuesService.appStatus.notInstalled;
            }
        // windows & osx
        } else {
            // with agent - agent is not installed, does not have parental installed, open install modal, send link step
            if (device.agent_status !== this.valuesService.agentStatus.INSTALLED) {
                return this.parentalValuesService.appStatus.notInstalled;
            // with agent - agent is installed, does not have parental installed, open install modal, wait for parental installed event step
            } else if (!this.iParentalService.hasParentalNoAgent(device)) {
                if (!this.iParentalService.parentalIsEnabled(device)) {
                    return this.parentalValuesService.appStatus.notInstalled;
                } else {
                    return this.parentalValuesService.appStatus.pending;
                }
            // with agent - agent is installed, parental is installed
            } else {
                return this.parentalValuesService.appStatus.installed;
            }
        }
    }

    createParentalDevicesInterface(profileHash) {
        const profiles = this.profilesService.getParentalProfiles();
        const profileDevices  = this.usefulService.getNested(profiles, {}, profileHash, "devices_monitored", "all");

        for (const id in profileDevices) {
            if (!profileDevices[id].processed) {
                profileDevices[id].processed = {};
            }

            if (!profileDevices[id].processed.interfaceParental) {
                profileDevices[id].processed.interfaceParental = {};
            }

            profileDevices[id].processed.interfaceParental.parentalAppStatus = this.checkAppStatus(profileDevices[id]);
        }
    }

    // folosita pt a inchide bannerul chiar daca requestul nu s-a facut
    // ar fi ciudat sa nu inchizi un banner cand apesi pe x, chiar daca in spate se face un request
    // userul nu stie asta si nici nu trebuie sa afle
    setDevicesLimitReachedInterfaceShow(newValue) {
        const devicesObj = this.devicesService.getDevicesList();
        if (devicesObj?.interface?.limitReached?.showBanner !== undefined) {
            devicesObj.interface.limitReached.showBanner = newValue;
        }
    }

    getDevicesLimitReachedInterface() {
        const devicesObj = this.devicesService.getDevicesList();
        return devicesObj?.interface?.limitReached;
    }

    // calculeaza urmatorul pas cu care va fi setat bannerul in backend
    getDevicesLimitReachedInterfaceNextStep() {
        const devicesObj = this.devicesService.getDevicesList();
        const currentStep = devicesObj?.interface?.limitReached?.step;

        // daca suntem la ultimul pas, urmatorul pas va fi tot ultimul pas
        if (currentStep === this.devicesValuesService.limitReachedStepDays.STEP6) {
            return this.devicesValuesService.limitReachedStepDays.STEP6;
        }

        // daca suntem la pasul 0 (sau nu avem niciun pas salvat in backend) urmatorul pas e 1
        if (!currentStep) {
            return this.devicesValuesService.limitReachedStepDays.STEP1;
        }

        // altfel urm pas e pasul curent + 1
        return currentStep + 1;
    }

    bannerSettingNeedsReset() {
        const devicesObj = this.devicesService.getDevicesList();
        return devicesObj?.interface?.limitReached?.resetBannerSetting;
    }

    getTheOnlyFullService() {
        const devicesObj = this.devicesService.getDevicesList();
        return devicesObj?.interface?.limitReached?.service;
    }

    createDevicesInterfaceLimitReached() {
        const devicesObj = this.devicesService.getDevicesList();
        if (!devicesObj.interface) {
            devicesObj.interface = {};
        }

        const limitReached = {
            content: {},
            step: this.devicesValuesService.limitReachedStepDays.STEP0,
            showBanner: false,
            service: null,
            resetBannerSetting: false
        };

        const bannerSetting = this.settingsService.getLimitReachedBannerSetting();
        // daca nu avem limit reahed pt niciun service
        if (!this.subscriptionsService.checkForLimitReachedBannerAppearance()) {
            // dar avem informatii pt banner
            if (bannerSetting) {
                // il resetam
                limitReached.resetBannerSetting = false;
            }
            // si nu il afisam pt ca nu avem indeplinita conditia de afisare limit reached
            limitReached.showBanner = false;
            return;
        }

        // daca avem limit reached pt cel putin 1 service si avem si info pt banner
        if (bannerSetting) {
            // daca info pt banner nu e valida o resetam
            if (!tv4.validate(bannerSetting, schemas.schemaBannerSetting)) {
                limitReached.resetBannerSetting = true;
                return;
            } else {
                // extragem info pt banner
                const bannerSettingArray = bannerSetting.split(":");
                // ultima data cand userul a dat close banner
                const timestamp = parseInt(bannerSettingArray[0], 10);
                // pasul curent al bannerului
                const step = parseInt(bannerSettingArray[1], 10);

                // daca pasul curent e in afara limitelor acceptate, ascundem banner
                if (step < this.devicesValuesService.limitReachedStepDays.STEP0 || step > this.devicesValuesService.limitReachedStepDays.STEP6) {
                    limitReached.showBanner = false;
                    return;
                }

                // daca timestampul e mai mare decat cel curent, resetam banner (asta inseamna informatie invalida)
                const currentTimestamp = new Date().getTime();
                if (timestamp > currentTimestamp) {
                    limitReached.resetBannerSetting = true;
                } else {
                    // calculam pt pasul curent al bannerului daca e cazul sa il afisam
                    // la fiecare pas, bannerul se afiseaza daca a trecut un numar fix de zile
                    // daca nu au trecut zilele corespunzatoare pasului la care ne aflam, nu afisam banner
                    const oneDayTimestamp = this.valuesService.SECONDS_IN_A_DAY * 1000;
                    const daysToPass = this.devicesValuesService.limitReachedStepDays[`STEP${step}`];
                    if (currentTimestamp - timestamp < daysToPass * oneDayTimestamp) {
                        limitReached.showBanner = false;
                        return;
                    }
                    limitReached.step = step;
                }
            }
        }

        const services = this.subscriptionsService.getAllServices();
        // daca exista 1 singur service si acel service e full
        if (services.length === 1) {
            limitReached.content = this.devicesValuesService.limitReachedCases['oneSubs'];
            limitReached.service = services[0];
        } else {
            // daca avem mai mult de 1 serviciu si cel putin 1 serviciu e full
            limitReached.content = this.devicesValuesService.limitReachedCases['moreSubs'];
        }

        devicesObj.interface.limitReached = limitReached;
    }

    displayManageDuplicatesBanner() {
        const clonedArr = this.getClonedDevices(); 
        return clonedArr.length > 0 && !sessionStorage.getItem(this.devicesValuesService.duplicatesBannerInStorage);
    }

    getClonedDevices() {
        let clonesArr = [];
        const devicesObj = this.devicesService.getDevicesList();
 
        for (let duplicate of devicesObj.clones) {
            let item = {};
            item["device_id"] = duplicate.device_id;
            item["display_name"] = duplicate.display_name;
            item["totalClones"] = 0;
            item["clones"] = [];

            const clones = Array.isArray(duplicate?.clones) ? duplicate.clones : [];
            for (let clone of clones) {
                let clonedDevice = this.devicesService.retrieveDeviceById(clone);
                if (clonedDevice) {
                    let newClone = {}
                    newClone["device_id"] = clonedDevice.device_id;
                    newClone["display_name"] = clonedDevice.display_name;
                    newClone["onbox_timestamp"] = clonedDevice.onbox_timestamp;

                    item["totalClones"] ++;
                    item["clones"].push(newClone);
                }
            }

            if (item["clones"].length > 0) {
                clonesArr.push(item);
            }
        }

        return clonesArr;
    }

    /**
     * Computes the title for the devices page
     * @returns {string} The title for the devices page
     */
    public computeDevicesTitle(): string {
        if (this.profilesService.isOwnerVSBAdmin()) {
            return 'devices.main.title.vsb';
        }
        if (this.profilesService.isFamilyPlanAdmin()) {
            return 'devices.main.title.family';
        }
        return 'devices.main.title';
    }

    /**
     * Computes the text for the empty state on the devices page
     * @returns {string} The title for the empty state on the devices page
     */
    public computeEmptyStateDevicesGroupedText(): string {
        if (this.profilesService.isOwnerVSBAdmin()) {
            return 'dashboard.devices.emptyState.install.noDevices.employees';
        }
        if (this.profilesService.isFamilyPlanAdmin()) {
            return 'dashboard.devices.emptyState.install.noDevices.family';
        }
        return null;
    }

}
